import { Checkbox, Flex, Icon, READABLE_LINE_MAX_WIDTH, Text, TextField, Button, createMarkup, Rte, useModal, INNER_MAX_WIDTH, scale, breakpoints } from '@capioweb/ui-components/src'
import { BooleanProperty, LinkProperty, StringProperty } from '../../@types/EpiFoundationSpa/Property'
import React from 'react'
import { LocalesContext } from '../../context'
import { SharedFormProps } from '../../@types/CapioWeb/core'
import { useForm } from 'react-hook-form'
import { postAccountForm } from '../MyPages/api/postAccountForm'
import { InfoModal } from '../../pages/MyPagesUserRegistrationPage/InfoModal'
import { StyledButtonContent, StyledIcon } from '../MyPagesUserLifestyleFormBlock/LifestyleForm'
import { getAntiforgeryToken } from '../../utils/antiforgery'
import { isLocalStorageAvailable } from '../../utils'

interface MyPagesSubscribeBlockProps {
    heading: StringProperty
    description: StringProperty
    subscribeForm: SharedFormProps
    leftAligned?: BooleanProperty
    topMargin?: BooleanProperty
}

export const MyPagesSubscribeBlock: React.FC<MyPagesSubscribeBlockProps> = ({ heading, description, leftAligned, subscribeForm, topMargin }) => {
    const { translate, translationKeys } = React.useContext(LocalesContext)
    const [loading, setLoading] = React.useState<boolean>(false)
    const [hideBlock, setHideBlock] = React.useState<boolean>(false)


    // Register form
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        mode: "onChange",
    })

    // Submit handler
    const onSubmit = async (data) => {

        setLoading(true)

        const { action } = subscribeForm
        const antiForgeryToken = await getAntiforgeryToken()
        try {
            const result = await postAccountForm(
                action,
                antiForgeryToken,
                data,
                subscribeForm.method
            )

            if (result.success) {
                setFeedbackMsg({
                    success: true,
                    message: translate(
                        translationKeys.manageAccountFeedbackMessageSuccess
                    ),
                })
            } else {
                setFeedbackMsg({
                    success: false,
                    message: result.data?.message ?? "Något gick fel",
                })
            }
        } catch (e) {
            console.error("---Catch", e, "--- error")
        }

        setLoading(false)

    }

    // Get props for submit button
    const submitButtonProps = subscribeForm.fields.find(
        (field) => field.type === "submit"
    )

    // Feedback message
    const [feedbackMsg, setFeedbackMsg] =
        React.useState<{
            success: boolean
            message: string
        }>(null)

    // Modal hook
    const { addModal } = useModal()


    React.useEffect(() => {
        if (isLocalStorageAvailable()) {
            if (localStorage.getItem('hideMyPagesSubscribeBlock')) setHideBlock(true)
        }

    })

    if (hideBlock) return null


    return (
        <Flex as="section" w={{ _: 1, m: 'calc(100% - 48px)' }} mx='auto' maxw={INNER_MAX_WIDTH} mt={topMargin?.value ? 3 : 0}>
            <Flex w={1} maxw={leftAligned?.value ? READABLE_LINE_MAX_WIDTH : INNER_MAX_WIDTH} position='relative' mx='auto' px={{ _: 0, m: 3 }} mb={3} backgroundColor="purple33" borderRadius={{ _: 0, m: 24 }} ml={leftAligned.value ? 0 : 'auto'} >
                <Flex maxw={INNER_MAX_WIDTH} w={1} mx='auto'>
                    <Flex
                        maxw={READABLE_LINE_MAX_WIDTH}
                        w={1}
                        m={leftAligned?.value ? 0 : "0 auto"}
                    >
                        <Flex
                            as='button'
                            position="absolute"
                            top={{ _: "15px", m: "32px" }}
                            right={{ _: "15px", m: "32px" }}
                            onClick={() => {
                                if (isLocalStorageAvailable()) localStorage.setItem('hideMyPagesSubscribeBlock', '1')
                                setHideBlock(true)
                            }
                            }
                        >
                            <Icon icon="close"></Icon>
                        </Flex>
                        <Flex
                            w={1}
                            px={3}
                            pt={{ _: 7, m: 5 }}
                            pb={5}
                            justifyContent="center"
                        >
                            <Flex
                                flexGrow={1}
                                flexDirection="column"
                            >

                                {heading?.value && (
                                    <Text
                                        variant="headline-2"
                                        mb={2}
                                        textAlign={leftAligned?.value ? "left" : "center"}
                                    >
                                        {heading?.value}
                                    </Text>
                                )}
                                {description?.value && (
                                    <Rte>
                                        <div dangerouslySetInnerHTML={createMarkup(description?.value)}></div>
                                    </Rte>
                                )}


                                <Flex
                                    as="form"
                                    action={subscribeForm.action}
                                    encType="application/x-www-form-urlencoded"
                                    method={subscribeForm.method}
                                    onSubmit={handleSubmit(onSubmit)}
                                    flexDirection="column"
                                >
                                    {subscribeForm.fields.map((field) => {
                                        const isInputField = !["submit"].includes(field.type)
                                        const requiredFields = [
                                            "terms",
                                        ]

                                        if (field && field.type && isInputField) {
                                            const { type, value, required, ...rest } = field
                                            const { ref, ...registerProps } = register(field.name, {
                                                required: {
                                                    value:
                                                        requiredFields.includes(field.name) && !field.disabled
                                                            ? true
                                                            : false,
                                                    message:
                                                        subscribeForm.requiredMessage !== ""
                                                            ? subscribeForm.requiredMessage
                                                            : translate(translationKeys.requiredFieldMessage),
                                                },
                                                ...(field.validators?.length > 0 && {
                                                    pattern: {
                                                        value: new RegExp(field.validators[0].pattern),
                                                        message: field.validators[0].message,
                                                    },
                                                }),
                                            })
                                            const fieldProps = {
                                                ...registerProps,
                                                ...rest,
                                                type,
                                                key: field.id,
                                                error: errors[field.name],
                                                ...(field.type !== "hidden" &&
                                                    errors[field.name] && {
                                                    helperText: errors[field.name].message,
                                                }),
                                            }

                                            if (["text", "email", "tel"].includes(type)) {
                                                return (
                                                    <TextField inputRef={ref} defaultValue={value} {...fieldProps} />
                                                )
                                            } else {
                                                return {
                                                    checkbox: (
                                                        <Flex key={field.id} mb={2} maxw={{ m: 55 }} mx={leftAligned?.value ? 0 : 'auto'} justifyContent={'space-between'} columnGap={3} w={{ _: 1, m: leftAligned?.value ? 1 : 55 }}>
                                                            <Checkbox
                                                                inputRef={ref}
                                                                value={
                                                                    field.items?.length > 0
                                                                        ? field.items[0].value
                                                                        : field.label
                                                                }
                                                                defaultChecked={
                                                                    field.items?.length > 0 && field.items[0].checked
                                                                }
                                                                {...fieldProps}
                                                            />
                                                            {subscribeForm.modals?.find((m) => m.id === field.id) && (
                                                                <Flex
                                                                    as="button"
                                                                    aria-label={`${translate(translationKeys.openModalFor)} ${field.label
                                                                        }`}
                                                                    flexShrink={0}
                                                                    onClick={(e) => {
                                                                        e.preventDefault()
                                                                        addModal(
                                                                            <InfoModal
                                                                                lastFocusedElement={e.target}
                                                                                {...subscribeForm.modals.find((m) => m.id === field.id)}
                                                                            />
                                                                        )
                                                                    }}
                                                                >
                                                                    <Icon
                                                                        icon="info-circle"
                                                                        size={3}
                                                                    // style={{ pointerEvents: "none" }}
                                                                    />
                                                                </Flex>
                                                            )}
                                                        </Flex>
                                                    ),
                                                    hidden: (
                                                        <input
                                                            ref={ref}
                                                            readOnly
                                                            key={field.value}
                                                            value={field.value}
                                                            {...fieldProps}
                                                        />
                                                    ),
                                                }[type]
                                            }
                                        }
                                    })}
                                    {submitButtonProps && (
                                        <Flex flexDirection="column" maxw={{ m: 'fit-content' }} mx={{ m: 'auto' }} ml={leftAligned?.value ? { m: 0 } : { m: 'auto' }} >
                                            <Button mt={1} mx={leftAligned?.value ? 0 : "auto"} style={{ position: 'relative', width: '100%' }} type={loading ? 'Button' : "submit"}>
                                                <StyledIcon aria-hidden loading={loading}>
                                                    <Icon icon="ellipse" color='white' />
                                                </StyledIcon>
                                                <StyledButtonContent loading={loading}>{translate(translationKeys.save)}</StyledButtonContent>
                                            </Button>
                                            {/* Feedback message - used if we don't redirect (errors & success) */}
                                            <Flex
                                                h={feedbackMsg ? "auto" : "0"}
                                                overflow="hidden"
                                                w={1}
                                                mt={feedbackMsg ? 1.5 : 0}
                                                justifyContent='end'
                                                role="region"
                                                aria-live="polite"
                                            >
                                                <Text
                                                    as="span"
                                                    textColor={feedbackMsg?.success ? "primary" : "alert"}
                                                >
                                                    {feedbackMsg?.message}
                                                </Text>
                                            </Flex>
                                        </Flex>
                                    )}
                                </Flex>
                            </Flex>
                        </Flex>
                    </Flex>
                </Flex>
            </Flex>
        </Flex >
    )
}
